import React, { useEffect, useMemo, useRef, useState } from 'react'
import { X, ChevronDown, ChevronRight, Radio, MessageSquare, Loader2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { SessionCallsState, UseEntityActivityLogResult } from '../hooks/useEntityActivityLog.js'
import { actionLabel, actionColor } from '../utils/auditLabels.js'
import { TOOL_CATEGORIES, toolCategory, type ToolCategory } from '../utils/toolCategory.js'
import type { AiToolCallEvent, SessionSummary } from '../types/index.js'

interface Props extends UseEntityActivityLogResult {
  entityId: string | null
  title: string
  subtitle?: string
  noSessionsLabel: string
  onClose: () => void
}

type DateRange = 'all' | 'today' | '7d' | '30d'
type StatusFilter = 'all' | 'success' | 'error'

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
]

function withinDateRange(iso: string, range: DateRange): boolean {
  if (range === 'all') return true
  const t = new Date(iso).getTime()
  if (range === 'today') {
    const startOfToday = new Date()
    startOfToday.setHours(0, 0, 0, 0)
    return t >= startOfToday.getTime()
  }
  const days = range === '7d' ? 7 : 30
  return Date.now() - t <= days * 24 * 60 * 60 * 1000
}

function formatTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch {
    return iso
  }
}

function formatDateTime(iso: string) {
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

const ToolCallRow: React.FC<{ entry: AiToolCallEvent }> = ({ entry }) => {
  const { currentTheme } = useTheme()
  const [expanded, setExpanded] = useState(false)
  const isError = entry.status === 'error'

  return (
    <div
      className="rounded-xl p-3 border"
      style={{ backgroundColor: currentTheme.secondary, borderColor: currentTheme.border }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 text-left"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="text-sm font-semibold truncate" style={{ color: currentTheme.text }}>
          {entry.toolName}
        </span>
        <span
          className="ml-auto flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
          style={
            isError
              ? { backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText }
              : { backgroundColor: currentTheme.successBackground, color: currentTheme.successText }
          }
        >
          {entry.status}
        </span>
        <span className="flex-shrink-0 text-xs" style={{ color: currentTheme.mutedText }}>
          {entry.latencyMs}ms
        </span>
        <span className="flex-shrink-0 text-xs" style={{ color: currentTheme.mutedText }}>
          {formatTime(entry.createdAt)}
        </span>
      </button>

      {expanded && (
        <div className="mt-3 space-y-2 text-xs">
          <div>
            <p className="font-semibold mb-1" style={{ color: currentTheme.secondrytext }}>Arguments</p>
            <pre
              className="rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-words"
              style={{ backgroundColor: currentTheme.sectionBackground, color: currentTheme.text, border: `1px solid ${currentTheme.border}` }}
            >
              {safeStringify(entry.args)}
            </pre>
          </div>
          {isError ? (
            <div>
              <p className="font-semibold mb-1" style={{ color: currentTheme.secondrytext }}>Error</p>
              <pre
                className="rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-words"
                style={{ backgroundColor: currentTheme.sectionBackground, color: currentTheme.errorText, border: `1px solid ${currentTheme.border}` }}
              >
                {safeStringify(entry.error)}
              </pre>
            </div>
          ) : (
            <div>
              <p className="font-semibold mb-1" style={{ color: currentTheme.secondrytext }}>Output</p>
              <pre
                className="rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-words"
                style={{ backgroundColor: currentTheme.sectionBackground, color: currentTheme.text, border: `1px solid ${currentTheme.border}` }}
              >
                {safeStringify(entry.output)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const EMPTY_CALLS_STATE: SessionCallsState = { events: [], cursor: null, hasMore: false, loading: false, loaded: false }

const SessionGroup: React.FC<{
  session: SessionSummary
  open: boolean
  onToggle: (conversationId: string) => void
  callsState: SessionCallsState | undefined
  status: StatusFilter
  categories: Set<ToolCategory>
  onExpand: (conversationId: string) => void
  onLoadMore: (conversationId: string) => void
}> = ({ session, open, onToggle, callsState, status, categories, onExpand, onLoadMore }) => {
  const { currentTheme } = useTheme()
  const state = callsState ?? EMPTY_CALLS_STATE

  useEffect(() => {
    if (open && !state.loaded && !state.loading) onExpand(session.conversationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const filteredEvents = state.events.filter(
    (e) => (status === 'all' || e.status === status) && (categories.size === 0 || categories.has(toolCategory(e.toolName))),
  )

  return (
    <div className="rounded-2xl border" style={{ borderColor: currentTheme.border }}>
      <button
        type="button"
        onClick={() => onToggle(session.conversationId)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <MessageSquare size={14} style={{ color: currentTheme.mutedText }} />
        <span className="text-sm font-semibold truncate" style={{ color: currentTheme.text }}>
          Session · {session.conversationId}
        </span>
        <span className="ml-auto flex-shrink-0 text-xs" style={{ color: currentTheme.mutedText }}>
          {session.callCount} call{session.callCount !== 1 ? 's' : ''} · {formatDateTime(session.lastActivityAt)}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {state.loading && state.events.length === 0 ? (
            <p className="text-xs py-2 flex items-center gap-1.5" style={{ color: currentTheme.mutedText }}>
              <Loader2 size={12} className="animate-spin" /> Loading…
            </p>
          ) : filteredEvents.length === 0 ? (
            <p className="text-xs py-2" style={{ color: currentTheme.mutedText }}>
              {state.events.length === 0 ? 'No calls loaded.' : 'No calls in this session match the current filters.'}
            </p>
          ) : (
            filteredEvents.map((entry) => <ToolCallRow key={entry.id} entry={entry} />)
          )}
          {state.hasMore && (
            <button
              type="button"
              onClick={() => onLoadMore(session.conversationId)}
              disabled={state.loading}
              className="w-full text-center text-xs font-medium py-2 rounded-lg transition disabled:opacity-50"
              style={{ color: currentTheme.accent, backgroundColor: currentTheme.secondary }}
            >
              {state.loading ? 'Loading…' : 'Load earlier calls'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const ActivityLogDrawer: React.FC<Props> = ({
  entityId,
  title,
  subtitle,
  noSessionsLabel,
  onClose,
  accountEvents,
  sessions,
  sessionsHasMore,
  sessionsLoading,
  loadMoreSessions,
  sessionCalls,
  loadSessionCalls,
  isLive,
  error,
}) => {
  const { currentTheme } = useTheme()
  const isOpen = entityId !== null
  const listRef = useRef<HTMLDivElement>(null)

  const [activeTab, setActiveTab] = useState<'account' | 'sessions'>('sessions')
  const [dateRange, setDateRange] = useState<DateRange>('all')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [categories, setCategories] = useState<Set<ToolCategory>>(new Set())
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set())
  const defaultExpanded = useRef(false)

  const toggleSession = (conversationId: string) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev)
      if (next.has(conversationId)) next.delete(conversationId)
      else next.add(conversationId)
      if (next.size === 0) setCategories(new Set())
      return next
    })
  }

  const toggleCategory = (cat: ToolCategory) => {
    setCategories((prev) => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }

  const filtersActive = dateRange !== 'all' || status !== 'all' || categories.size > 0

  const clearFilters = () => {
    setDateRange('all')
    setStatus('all')
    setCategories(new Set())
  }

  const filteredAccountEvents = useMemo(
    () => accountEvents.filter((e) => withinDateRange(e.createdAt, dateRange)),
    [accountEvents, dateRange],
  )

  // Date range narrows which sessions are shown; status/category filter the calls
  // inside a session once it's loaded (fetching every session up front to filter
  // by tool/status would defeat the point of lazy-loading them).
  const filteredSessions = useMemo(
    () => sessions.filter((s) => withinDateRange(s.lastActivityAt, dateRange)),
    [sessions, dateRange],
  )

  useEffect(() => {
    if (!defaultExpanded.current && sessions.length > 0) {
      defaultExpanded.current = true
      setExpandedSessions(new Set([sessions[0].conversationId]))
    }
  }, [sessions])

  useEffect(() => {
    defaultExpanded.current = false
    setExpandedSessions(new Set())
  }, [entityId])

  return (
    <>
      <div
        className="fixed inset-0 z-40 transition-opacity duration-300"
        style={{
          backgroundColor: isOpen ? 'rgba(0,0,0,0.4)' : 'transparent',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
        onClick={onClose}
      />

      <div
        className="fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 w-full sm:w-[600px]"
        style={{
          backgroundColor: currentTheme.primary,
          borderLeft: `1px solid ${currentTheme.border}`,
          boxShadow: currentTheme.boxShadow,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {isOpen && (
          <>
            <div
              className="flex items-start justify-between px-5 py-4 border-b flex-shrink-0"
              style={{ borderColor: currentTheme.border }}
            >
              <div className="flex-1 min-w-0 pr-3">
                <h2 className="text-base font-semibold truncate" style={{ color: currentTheme.text }}>
                  {title}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  {subtitle && (
                    <span className="text-xs truncate" style={{ color: currentTheme.secondrytext }}>
                      {subtitle}
                    </span>
                  )}
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={
                      isLive
                        ? { backgroundColor: currentTheme.successBackground, color: currentTheme.successText }
                        : { backgroundColor: currentTheme.border, color: currentTheme.secondrytext }
                    }
                  >
                    <Radio size={10} />
                    {isLive ? 'Live' : 'Disconnected'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg transition"
                style={{ color: currentTheme.secondrytext }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
                }}
                aria-label="Close panel"
              >
                <X size={16} />
              </button>
            </div>

            {/* Tabs */}
            <div
              className="flex-shrink-0 flex items-center gap-1 px-5 pt-3 border-b"
              style={{ borderColor: currentTheme.border }}
            >
              {([
                { key: 'sessions' as const, label: 'Chat Sessions', count: sessions.length },
                { key: 'account' as const, label: 'Account Events', count: accountEvents.length },
              ]).map((tab) => {
                const isActive = activeTab === tab.key
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className="px-3.5 py-2 text-sm font-semibold transition border-b-2 -mb-px"
                    style={{
                      color: isActive ? currentTheme.accent : currentTheme.secondrytext,
                      borderColor: isActive ? currentTheme.accent : 'transparent',
                    }}
                  >
                    {tab.label}
                    <span
                      className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold"
                      style={{
                        backgroundColor: isActive ? `${currentTheme.accent}22` : currentTheme.border,
                        color: isActive ? currentTheme.accent : currentTheme.secondrytext,
                      }}
                    >
                      {tab.count}
                    </span>
                  </button>
                )
              })}
            </div>

            {/* Filters */}
            <div
              className="flex-shrink-0 px-5 py-3 border-b space-y-2.5"
              style={{ borderColor: currentTheme.border }}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value as DateRange)}
                  className="rounded-lg border px-2.5 py-1.5 text-xs font-medium outline-none"
                  style={{
                    backgroundColor: currentTheme.inputBackground,
                    color: currentTheme.text,
                    borderColor: currentTheme.border,
                  }}
                >
                  {DATE_RANGE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                {activeTab === 'sessions' && (
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as StatusFilter)}
                    className="rounded-lg border px-2.5 py-1.5 text-xs font-medium outline-none"
                    style={{
                      backgroundColor: currentTheme.inputBackground,
                      color: currentTheme.text,
                      borderColor: currentTheme.border,
                    }}
                  >
                    <option value="all">All statuses</option>
                    <option value="success">Success</option>
                    <option value="error">Error</option>
                  </select>
                )}

                {filtersActive && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs font-medium underline"
                    style={{ color: currentTheme.secondrytext }}
                  >
                    Clear filters
                  </button>
                )}
              </div>

              {activeTab === 'sessions' && expandedSessions.size > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {TOOL_CATEGORIES.map((cat) => {
                    const selected = categories.has(cat)
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold transition"
                        style={
                          selected
                            ? { backgroundColor: `${currentTheme.accent}22`, color: currentTheme.accent, border: `1px solid ${currentTheme.accent}` }
                            : { backgroundColor: 'transparent', color: currentTheme.secondrytext, border: `1px solid ${currentTheme.border}` }
                        }
                      >
                        {cat}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-6">
              {error && (
                <div
                  className="rounded-lg p-3 text-sm"
                  style={{ backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText }}
                >
                  {error}
                </div>
              )}

              {activeTab === 'account' && (
                filteredAccountEvents.length === 0 ? (
                  <p className="text-sm py-4" style={{ color: currentTheme.mutedText }}>
                    {accountEvents.length === 0 ? 'No account events yet.' : 'No account events match the current filters.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {filteredAccountEvents.map((log) => {
                      const color = actionColor(log.action, currentTheme)
                      return (
                        <div
                          key={log.id}
                          className="flex items-center justify-between rounded-xl p-3 border"
                          style={{ backgroundColor: currentTheme.secondary, borderColor: currentTheme.border }}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
                              style={{ backgroundColor: color.bg, color: color.text }}
                            >
                              {actionLabel(log.action)}
                            </span>
                            <span className="text-xs truncate" style={{ color: currentTheme.secondrytext }}>
                              by {log.performedByName}
                            </span>
                          </div>
                          <span className="flex-shrink-0 text-xs" style={{ color: currentTheme.mutedText }}>
                            {formatDateTime(log.createdAt)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )
              )}

              {activeTab === 'sessions' && (
                filteredSessions.length === 0 && !error ? (
                  <p className="text-sm py-4" style={{ color: currentTheme.mutedText }}>
                    {sessions.length === 0 && !sessionsLoading
                      ? noSessionsLabel
                      : sessionsLoading
                        ? 'Loading sessions…'
                        : 'No sessions match the current filters.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {filteredSessions.map((session) => (
                      <SessionGroup
                        key={session.conversationId}
                        session={session}
                        open={expandedSessions.has(session.conversationId)}
                        onToggle={toggleSession}
                        callsState={sessionCalls[session.conversationId]}
                        status={status}
                        categories={categories}
                        onExpand={loadSessionCalls}
                        onLoadMore={(id) => loadSessionCalls(id, true)}
                      />
                    ))}
                    {sessionsHasMore && (
                      <button
                        type="button"
                        onClick={loadMoreSessions}
                        disabled={sessionsLoading}
                        className="w-full text-center text-xs font-medium py-2.5 rounded-xl border transition disabled:opacity-50"
                        style={{ color: currentTheme.text, borderColor: currentTheme.border, backgroundColor: currentTheme.secondary }}
                      >
                        {sessionsLoading ? 'Loading…' : 'Load more sessions'}
                      </button>
                    )}
                  </div>
                )
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default ActivityLogDrawer
